/**
 * Banner shown in the side nav on the local dev server only. Local dev points
 * at a real remote backend, so work done here is not sandboxed — the banner
 * exists so that is never a surprise.
 */
export const LOCAL_DEV_BANNER = {
  FULL: 'Local dev build — not the deployed QA app',
  SHORT: 'Local dev',
}

/** Copy for the MS_BACKEND auth guard screens. */
export const MS_BACKEND_AUTH_STRINGS = {
  AUTHENTICATING: 'Authenticating…',
  SIGN_IN_FAILED: 'Sign-in failed',
  TRY_AGAIN: 'Try Again',
}

/**
 * Copy for the /logout confirmation screen. The button label stays on
 * `NAMESPACES.LOGIN_BUTTON` in constants.ts, which other screens already share.
 */
export const LOGOUT_PAGE_STRINGS = {
  TITLE: 'Logged Out',
  MESSAGE: 'You have been successfully logged out.',
  SUBTEXT: 'Thank you for using our application.',
}

export const STRINGS = {
  ZIP_CODE: 'ZIP Code',
  PROGRESS_BANNER_TEXT: 'Updates are in progress. ',
  PROGRESS_WORKFLOW_REFRESH_TEXT_START: 'please wait to proceed or click ',
  PROGRESS_WORKFLOW_REFRESH_TEXT_END: 'to reload manually.',
  RESET_WORKFLOW: 'Reset Workflow',
  ALLOW_POPUP_MESSAGE:
    'Please allow pop-up for this site to view the next step of the workflow',
  POI_NOT_PROVIDED: 'POI Not Provided',
  ASSIGN: 'Assign',
  ISACTIVE: 'isActive',
  ALL_ENTITIES: 'All Entities',
  MAX_SIZE_10_MB: 'Max size 10 MB',
  MAX_SIZE_20_MB: 'Max size 20 MB',
  MAX_SIZE: 'Max size ',
  JPG_PNG: '(JPG/PNG)',
  MP3_MP4: '(MP3/MP4)',
  CALL: 'Call',
  OUTGOING_CALL: 'Outgoing Call',
  SWAP: 'Swap',
  MERGE: 'Merge',
  HOLD_NEW: 'hold',
  CONNECTED: 'connected',
  CONNECTING: 'connecting',
  CONFERENCE: 'Conference',
  IN_CALL: 'In Call',
  UNKNOWN_CALLER: 'Unknown',
  RECORDING: 'Recording...',
  END_CALL: 'End call',
  CLOSE_CONTACT: 'Close Contact',
  DISCONNECT_CALL: 'Disconnect Call',
  CALL_DURATION: 'Call Duration',
  ACCEPT_CALL: 'Accept Call',
  REJECT_CALL: 'Reject Call',
  MUTE: 'Mute',
  UNMUTE: 'Unmute',
  HOLD: 'Hold',
  ON_HOLD: 'On Hold',
  TRANSFER: 'Transfer',
  ONLINE: 'online',
  AUDIT_HISTORY: 'Audit History',
  AUDIT_NOT_AVAILABLE_MESSAGE:
    'Audit History will be available shortly. You’ll be able to access detailed records of changes shortly.',
  CLOSE: 'Close',
  REQUIRED_FILEDS_MISSING_MESSAGES: 'Missing Information',
  QUEUE_NEGATIVE_SCREEN_VERBIAGE:
    "You don't have access to manage queues. Click on dropdown to check your assigned queues on sidebar",
  QUEUE_CREATION_MSG: 'Please enter the below information to create a queue',
  AVAILABLE: 'Available',
  QUEUE_UPDATION_MSG: 'Please enter the below information to update a queue',
  QUEUE_VIEW_MSG: 'View the queue details',
  TRANSFERRING_STATE: 'Transferring...',
  TRANSFERRING_FAILED_STATE: 'Transfer Failed',
  AGENT_SEARCH_PLACEHOLDER: 'Search by agent',
  AGENT_SEARCH_DATA_QA_ID: 'search-by-agent',
  REMOVE_UNREADABLE_DOCUMENTS: 'remove_unreadable_document',
  AE_PC_STATUS_FIELD: 'ae_pc_status',
  AE_PC_STATUS_UNREADABLE: 'UNREADABLE',
  BACK: 'Back',
  EDIT: 'Edit',
  SAVE: 'Save',
  CLEAR: 'Clear',
  NO_CASE_LAYOUT_ON_ENTITY: `The selected layout for this entity does not have assigned 'Agent' columns. Please add these column in the layout to proceed.`,
  NO_COMMS_LAYOUT_ON_ENTITY: `The selected layout for this entity does not have 'Reviewer' columns. Please add these column in the layout to proceed.`,
  CASE: 'Case',
  COMMUNICATION: 'communications',
  COMMUNICATION_RESENT: 'Communication resent successfully',
  ERROR_DIFF_NAME: 'To proceed, choose a different name.',
  QUEUE_ASSIGNMENT_UPDATION_TOAST: 'Queue Assignment Updated Successfully',
  QUEUE_ASSIGNMENT_TOAST: 'Queue Assigned Successfully',
  QUEUE_UPDATION_TOAST: 'Queue Updated Successfully',
  QUEUE_CREATION_TOAST: 'Queue created successfully',
  QA_360_BACK: '360-view-edit',
  QA_360_EDIT: '360-view-back',
  MULTI_UPLOAD_FIELDS_REQUIRED: {
    entity_name: 'document',
    select: ['attachment'],
  },
  MULTI_UPLOAD_FIELDS_AE_PC_REQUIRED: {
    entity_name: 'document',
    select: ['attachment', 'c_ae_pc_document'],
  },
  QUICK_DENIAL: 'Quick Denial',
  QUICK_DENIAL_REASON: 'Quick Denial Reason',
  QD_POP_UP_HEADING:
    'Patient request for PAP has been denied due to given reasons',
  PROCEED_TO_MI: 'Proceed to MI flow',
  PROCEED_TO_QD: 'Proceed to Quick Denial',
  APPLICATION_VERSION: 'Application Version',
  APPLICATION_FORM_VERSION: 'Application Form Version',
  ICDCODE: 'ICD Code',
  ICD_CODE: 'icd_code',
  ICD_CODES: 'icd codes',
  EXCEPTION_REASON: 'exception_reason',
  DIAGNOSIS: 'Diagnosis',
  VIEW: 'view',
  NEW_EDIT: 'edit',
  CASE_TYPE: 'case_type',
  REMOVED: 'REMOVED',
  LOGIN_AWS_ERROR:
    'You are not logged into AWS Connect. Please log in to update your availability status.',
  UNABLE_TO_FETCH_AVAIALABILITY:
    'Unable to fetch availability from AWS Connect. The displayed status may not be up-to-date.',

  UNABLE_GET_WORK: 'You do not have permission to access this work assignment.',
  DASHBOARD_TILE_ERROR: 'Error fetching dashboard data',
  DASHBOARD_TILE_VERBIAGE:
    'There seems to be a configuration issue, please contact your administrator.',
  REASSIGN_CASE: 'Reassign Case',
  UNASSIGN_CASE: 'Unassign Case',
  CLOSE_CASE: 'Close Case',
  VOID_CASE: 'Void Case',
  REOPEN_CASE: 'Reopen Case',
  REOPEN_STRING: 'reopen',
  UNASSIGN_CASE_MESSAGE: 'Are you sure you want to unassign this case?',
  CASE_COMPLETION_OVERRIDE_MESSAGE:
    'Do you want to update the Completion Date?',
  CLOSE_CASE_MESSAGE: 'Are you sure you want to close this case?',
  VOID_CASE_MESSAGE: 'Are you sure you want to void this case?',
  CLOSE_TOASTER_MSG: 'Case Closed Successfully!',
  CLOSED: 'Closed',
  VOID_TOASTER_MSG: 'Case Void Successfully!',
  REASSIGN_TOASTER_MSG: 'Case Reassigned Successfully!',
  UNASSIGN_TOASTER_MSG: 'Case Unassigned Successfully!',
  REOPEN_TOASTER_MSG: 'Case Reopened Successfully!',
  SOMETHING_WENT_WRONG: 'Something went wrong!',
  NO_PENDING_TASK_FOUND: 'No pending task found!',
  NO_USER_FOUND: 'No User Found',
  INVALID_MESSAGE: 'Invalid Message',
  SELECT_AGENT: 'Select An Agent',
  SUSPEND: 'suspend',
  CANCEL: 'cancel',
  OPEN: 'Open',
  VOID: 'Void',
  MISSING_INFORMATION: 'Missing Information',
  POLICY_UPDATE_SUCCESS_MSG: 'Policy update submitted successfully.',
  POLICY_UPDATE_ERROR_MSG:
    'Policy update could not be completed. Please try again.',
  POLICY_UPDATE_CONFIRM_MSG:
    "Are you sure you want to update the patient's insurance policy?\n\nThis action will update the insurance information and may affect downstream workflow processing.",
  NOTES: 'Notes',
  DOCUMENT_SUCCESSFULLY_ADDED_TO_ATTACHMENTS:
    'Document successfully added to Attachments',
  FAILED_TO_ADD_DOCUMENT_TO_ATTACHMENTS:
    'Failed to add document to Attachments',
  NO_DATA_AVAILABLE: 'No data available',
  MIGRATED_CASE_ERROR_MESSAGE:
    'This is a migrated case. You can not perform this case action',
  ERROR_CREATING_TASK: 'Error creating task',
  SHIPMENT_DETAILS: 'Shipments Details',
  INFLIGHT_DETAILS: 'Inflight Details',
  ENROLLMENT_ID: 'Enrollment ID',
  INACTIVE: 'INACTIVE',
  STATUS: 'Status',
  NO_DATA_FOUND: 'No data Found',
  OPEN_DOC_IN_NEW_TAB: 'Open Document in New Tab',
  VOID_STATUS_CHANGE_CONFIRMATION_MSG:
    'Are you sure you want to change the status from Void to Received?',
  DISCARDED_STATUS_CHANGE_CONFIRMATION_MSG:
    'Are you sure you want to change the status from Discarded to Received?',
  NO_LAYOUT_FOUND: 'No layout found',
  NO_LAYOUT_SELECTED: 'No layout selected',
  DATA_UPDATED_SUCCESSFULLY: 'Data updated successfully',
  INSURANCE_INFORMATION: 'Insurance Information',
  VA_OR_MILITARY: 'VA or Military: Has you applied for VA Benefits?',
  INSURANCE_TYPE: 'Insurance Type:',
  INDICATE_THIRD_PARTY_INSURANCE:
    'Indicate if your insurance plan or a third party requires you to apply to the Teva Cares Program as a condition, requirement, or prerequisite for coverage of a specific Teva Cares medication.',
  MEDICAID_STATUS_QUESTION:
    'Medicaid: What is your Medicaid status for the past 12 months?',
  PDF_LOAD_FAILED: 'Failed to load Document.',

  PATIENT_PRESCRIPTION_COVERAGE: 'Does the patient have prescription coverage?',
  EMPLOYER_SUPPLIED_OR_PRIVATE_DRUG_COVERAGE:
    'Employer-supplied or private drug coverage',
  VA_OR_MILITARY_BENEFITS: 'VA or Military Benefits',
  MEDICARE_ADVANTAGE_PART_C: 'Medicare Advantage (Part C)',
  MEDICARE_PART_D_PRESCRIPTION_COVERAGE_PART_D_PLAN:
    'Medicare (Part D) Prescription Coverage Part D Plan',
  MEDICARE_PART_B_MEDICAL_COVERAGE:
    'Medicare Part B (Medical Coverage Which Covers Some Prescription Products)',
  MEDICAID_PRESCRIPTION_DRUG_COVERAGE: 'Medicaid Prescription Drug Coverage',
  MEDICARE_LOW_INCOME_SUBSIDY_LIS_EXTRA_HELP:
    'Medicare Low Income Subsidy (LIS/ Extra Help)',
  PART_D_PLAN_NAME: 'Part D Plan',
  UNINSURED: 'Uninsured',
  NEXT: 'Next',
  PREV: 'Prev',
  NO_DOCUMENT_FOUND: 'No Document Found',
  WITHDRAW: 'Withdraw',
  WITHDRAWN_STATUS: 'Withdrawn',
  PAP_ENROLLMENT_STATUS: 'pap_enrollment_statuses',
  CREATE_ORDER: 'Create Order',
  CHARACTER_LIMIT_50: 'Character Limit: 50',
  FILTER_VIEW_SAVE_AS: 'Filter View | Save as',
  FILTER_VIEW_SAVE: 'Filter View | Save',
  ERROR_SAVING_FILTER: 'Error saving filter',
  ACTIVE: 'Active',
  IN_ACTIVE: 'Inactive',
  WITHDRAWN: 'Withdrawn',
  PENDING: 'Pending',
  FILL_TYPE: 'Fill Type',
  COMMUNICATION_STATUS: 'communication_status',
  SHIPPING_DETAILS: 'Shipping Details',
  FULFILLMENT_TYPE: 'Fulfillment Type',
  ORDER_CASE_MESSAGE: 'Order Case Created Successfully',
  HEADING_FOR_ORDER_CASE: 'Order Creation',
  PATIENT: 'patient',
  FACILITY: 'facility',
  OFFICE: 'office',
  QUICK_DENIAL_SUCCESS: 'The case has been moved to Quick Denial Successfully',
  SHIP_TO_ENTITY: 'Ship To Entity',
  ADDRESS_LINE_1: 'Address Line 1',
  ADDRESS_LINE_2: 'Address Line 2',
  CITY: 'City',
  STATE: 'State',
  ZIPCODE: 'Zip Code',
  ERROR_DELETING_VIEW: 'Error deleting view',
  ERROR_FETCHING_VIEWS: 'Error fetching views',
  DEFAULT_VIEW: 'Default View',
  DO_YOU_WANT_TO_DELETE_VIEW: 'Do you want to delete ',
  VIEW_QUESTION_MARK: ' View?',
  FILL_REQUIRED_FIELDS: 'Please fill in all required fields.',
  NOVO_CLIENT_SHIP_TO_PATIENT: 'ship_to_patient',
  SHOW_INBOUND_SMS: 'show_inbound_sms',
  DEMO_DASHBOARD_CHANGES: 'demo_dashboard_changes',
  MESSAGE_FOR_ADDRESS_NOT_FOUND_CREATE_ORDER:
    'Primary address not found for the patient. Please update patient details before proceeding.',
}

export const PHARMACY_TRIAGE_STRINGS = {
  ELIGIBILITY_ASSESSMENT: 'Assessment Eligibilty for Fill',
  CALCULATE_QUEUED_DATE: 'Calculate Queued Date',
}

export const SP_FILL_DETAILS_STRINGS = {
  UPDATE_SP_FILL_DETAILS: 'Update SP Fill Details',
}

export const CALL_INTERACTION_STRINGS = {
  CALL_INTERACTION: 'Interaction',
  CREATE_NEW_INTERACTION: 'Create Interaction',
  INTERACTION_ID: 'Interaction ID',
  AGENT_ID: 'Agent ID',
  AGENT_NAME: 'Agent Name',
  AGENT: 'agent',
  TIME_TO_ANSWER: 'Time to Answer',
  REQUESTER_NUMBER: 'Requester Number',
  SEARCH_ENTITY: 'Search Entity',
  RECORDS_FOUND: 'Records Found',
  NO_RECORDS_FOUND: 'No Records Found',
  SEARCH: 'Search',
  CLIENT_INFORMATION: 'Client Information',
  PROGRAM_NAME: 'Program Name',
  SERVICE: 'Service',
  PRODUCT: 'Product',
  SELECT_ENTITY: 'Select Entity',
  INFORMATION: 'Information',
  REVIEW_MESSAGE_START: 'Review the',
  REVIEW_MESSAGE_END: 'Details Below for Confirmation',
  NO_ACTIVE_CALL_MESSAGE:
    'No active interaction at the moment.\nPlease check back when a call is ongoing.',
  CHANGES_DISCARDED_SUCCESSFULLY: 'Changes discarded successfully',
  ERROR_DISCARDING_CHANGES: 'Error discarding changes',
}

export const APPLICATION_PREFERENCES_STRINGS = {
  MATCH_RESULT: 'Match Result',
  CASE_ASSIGNMENT_CONFIGURATION_MESSAGE:
    'Please define a threshold limit for cases assigned to an agent',
  SHIFT_TIME_ZONE_MESSAGE:
    'Please select the shift time zone for the application',
  SHIFT_TIMINGS_MESSAGE:
    'Please fill the below details for configuring shift timings as per the Time Zone selected',
  SHIFT_NAME_UNIQUE: 'Shift Name must be unique.',
  REQUIRED_FIELD: 'This is a required field',
  POSITIVE_VALUE: 'The value must be positive.',
  MINIMUM_CANNOT_EXCEED_MAXIMUM:
    'Minimum number of cases cannot exceed maximum number.',
  START_TIME_CANNOT_LATER_END_TIME: 'Start Time cannot be equal to End Time.',
  END_TIME_CANNOT_LATER_START_TIME: 'End Time must be later than Start Time.',
  SHIFT_MUST_BE_MAXIMUM_8_HOURS: 'Shift must be maximum of 8 hours',
  DISABLE_SHIFT: 'Do you really want to disable this shift?',
  ENABLE_SHIFT: 'Do you really want to enable this shift?',
  NAME_ALREADY_EXIST: 'Shift name must be unique',
  SHIFT_ADDED_MESSAGE: 'Shift added successfully',
  SHIFT_UPDATED_MESSAGE: 'Shift updated successfully',
  SETTINGS_UPDATED_MESSAGE: 'General Settings updated successfully',
  SETTINGS_ADDED_MESSAGE: 'General Settings added successfully',
  SETTINGS_ADDED_ERROR_MESSAGE: 'Error occured',
  ADVANCED_SEARCH: 'Advanced Search',
  POTENTIAL_MATCH_LIST: 'Potential Match List',
  CANCEL: 'Cancel',
  UPDATE: 'Update',
  SUBMIT: 'Submit',
  SELECT: 'Select',
  NEXT: 'Next',
  DUPLICATE_HEADING: 'Potential Duplicates Found',
  CREATE_ANYWAY: 'Create Anyway',
  USE_EXISTING: 'Use Existing',
  MATCH_SCORE: 'Match Score',
  FOUND: 'found',
  RECORD: 'record',
  RECORDS_FOUND: 'records found',
}
export const CALL_POPUP_STRINGS = {
  CALL_TRANSFER_HEADING: 'Initiate Call Transfer',
}

export const PRINT_MAIL_STRING = {
  SINGLE_PROCESSING_PRINTING: "Letter updated to 'Processing Printing' status.",
  SINGLE_COMPLETED: "Letter updated to 'Completed' status.",
  SINGLE_DISCARD: "Letter updated to 'Discarded' status.",
  PROCESSING_PRINTING:
    "Selected letters updated to 'Processing Printing' status",
  COMPLETED: "Selected letters updated to 'Completed' status.",
  DISCARDED: "Selected letters updated to 'Discarded' status.",
  VOIDED: "Selected letters updated to 'Void' status.",
}

export const MAX_RTE_STRINGS = {
  ACCORDION_DISPLAY: 'MaxRTE API Responses',
  REQUIRED_ERROR:
    'Input fields are missing to check eligibility | External Trace Id : ',
  MISMATCH_ERROR: 'Data Mismatch with Patient Details | External Trace Id : ',
  APPROVE_TOASTER_MSG: 'Patient Insurance Information Updated Successfully!',
  ELIGIBILITY_ERROR_MSG: 'API Failed to fetch details',
  REPLICATE_TO_INSURANCE_ERR: 'Failed to replicate data',
  CHECK_ELIGIBILITY: 'Check Eligibility',
  HIPAA_CONSENT_MISSING: 'Hipaa Consent is missing to check eligibility',
  HIPAA_CONSENT_MISSING_PRODUCT: 'HIPAA consent missing for the product',
  HIPAA_CONSENT_MISSING_CASE: 'HIPAA consent missing for this PAP case',
  REQUIRED_FIELD_MISSING: 'Input fields are missing to check eligibility.',
  PATIENT_REQUIRED: 'Patient Information is required for Eligibility Check',
  PRODUCT_REQUIRED: 'Product is required for Eligibility Check',
  PATIENT_PRODUCT_REQUIRED:
    'Patient Information and Product is required for Eligibility Check',
  PRODUCT_MODAL_HEADING: 'Patient Eligibility',
  VALID_RECORD_EXIST_TITLE: 'Active e-Insurances Found',
  VALID_RECORD_EXIST:
    'Active e-Insurances already exists for this patient. Please review existing e-insurance details in Patient Information.',
}

export const CORRESPONDENCE_LOGS_STRINGS = {
  CORRESPONDENCE_LOGS: 'Correspondence Logs',
  API_ERROR: 'Error in API request:',
  GLOBAL: 'Global',
  SELECT_DATE_RANGE: 'Select Date Range',
  RESEND_SUCCESSFUL_MSG: ' resent successfully',
  RESEND_ERROR_MSG: 'Failed to resend correspondence. Please try again.',
  DOWNLOAD_ZIP_SUCCESS_MSG: 'Documents downloaded successfully.',
  DOWNLOAD_SUCCESS_MSG: 'Document downloaded successfully.',
  DOWNLOAD_ZIP_ERROR_MSG: 'Failed to download the documents.',
  NO_DOCUMENTS_MSG: 'No documents to download.',
  DOWNLOAD_ERROR_MSG: 'Failed to download the document.',
  CUSTOM: 'CUSTOM',
}

export const TABLE_ACTIONS_NAMESPACE = {
  EDIT: 'Edit',
  VIEW: 'View',
  VIEW_CORRESPONDENCE: 'View Correspondence Details',
  DELETE: 'Delete',
  DOWNLOAD: 'Download',
  DOWNLOAD_PDF: 'Download as PDF',
  DOWNLOAD_ZIP: 'Download as Zip',
  RESEND: 'Resend',
  RESEND_LOWERCASE: 'resend',
  RESEND_CORRESPONDENCE: 'Resend Correspondence',
  COPY_TEXT: 'Copy Content',
  PRINT_DOCUMENT: 'Print Document',
}

export const MULTI_UPLOAD_UTILS = {
  API_ERROR_MESSAGE: 'Error in API request:',
  ADDED_STATE_UPLOAD: 'ADDED',
  ENTITY_NAME: 'd_file_upload',
}

export const USER_PROFILE_MANAGEMENT = {
  STANDARD_USER: 'Standard User',
}

export const PERMISSION_ASSIGNMENT = {
  PERMISSION_ASSIGNMENT: 'Permission Assignment',
  PERMISSION_ASSIGNMENT_HEADING: 'List of the user and their assigned programs',
  ASSIGN_PERMISSIONS: 'Assign Permissions',
  NO_DATA_MESSAGE:
    'No user found. Click on "Assign Permissions" to include the users',
  USER_ID: 'User ID',
  FIRST_NAME: 'First Name',
  LAST_NAME: 'Last Name',
  TERRITORY: 'Territory',
  PROGRAM: 'Program',
  USER_ID_KEY: 'email',
  FIRST_NAME_KEY: 'first_name',
  LAST_NAME_KEY: 'last_name',
  TERRITORY_KEY: 'territory',
  PROGRAM_KEY: 'program_id',
  PROFILE: 'Profile',
  PROFILE_KEY: 'profile',
  ACTIONS: 'Actions',
  ACTIONS_KEY: 'actions',
  PERMISSION_SET_KEY: 'permission_sets',
  PERMISSION_SETS: 'Permission Sets',
  PROGRAM_PERMISSION_SET_TABLE_EMPTY_MESSAGE:
    'No permission sets assigned yet. Click “Add” to assign permission sets.',
  PROGRAM_NAME_KEY: 'program_name',
  CONFIRM_DIALOG_MESSAGE:
    'You do not have permission to view this module. You will be directed to Home page. \nDo you still want to proceed?',
  ADMIN: 'ADMIN',
  GLOBAL: 'GLOBAL',
  ASSIGN_PERMISSION_BTN_TOOLTIP: 'Assign Permissions to new users.',
  DISABLE_MODAL_MESSAGE:
    "Disabling this will revoke user's access from this program and automatically unassign user from all active assignments within program. Do you want to continue?",
  DISABLE_MODAL_TITLE: 'Disable User Program Permissions',
  ACCESS_RESTRICTED_FOR_CLIENT_MESSAGE:
    "You don't have access to manage permission sets in this client. Please contact system administrator.",
  UAM_SUPPORT_ONLY_TOOLTIP:
    'User Access Management can only be assigned to Support Users.',
}

export const CALL_INTERACTIONS = {
  INTERACTION: 'Interaction',
  INTERACTION_LOWERCASE: 'interaction',
  SEARCH_REQUESTOR_INFORMATION: 'Search Requester Information',
  SEARCH_RECORDS: 'Search Records',
  INTERACTION_ID: 'Interaction ID',
  AGENT_EMAIL: 'Agent Email',
  AGENT_NAME: 'Agent Name',
  PROGRAM: 'Program',
  PRODUCT: 'Product',
  CHANNEL: 'Channel',
  SEARCH_REQUESTOR_HEADING:
    'Please use the fields below to search for the requester information',
  LINK_INTERACTION_MESSAGE:
    'Are you sure you want to link this Interaction ID to the selected case?',
  LINK: 'Link',
  LINK_TOOLTIP: 'Link Interaction ID',
  LINK_TOASTER_SUCCESS_MESSAGE:
    'Interaction ID linked to the case successfully.',
  AWS_CONTACT_ID: 'AWS Contact ID',
  VIEW_ID: 'View ID',
  LINK_DETAILS: 'Link Details',
  DOCUMENT_TYPE: 'Document Type',
  CASE_DOCUMENT_TYPE: 'Case Document Type',
  INTAKE: 'Intake',
  NON_INTAKE: 'Non-Intake',
  INTAKE_FORM: 'Intake Form',
  NON_INTAKE_FORM: 'Non-Intake Form',
}

export const MANAGE_QUEUES_MESSAGES = {
  DELETE_TOASTER_MSG: 'Queue Deleted Successfully',
  ENABLE_MAIL_QUEUE_INBOUND_DISABLED:
    'Inbound Mail is disabled. To receive mails in the intake queue, you need to enable Inbound Mail in Program Configuration.Do you want to continue?',
  DISABLE_MAIL_QUEUE_INBOUND_ENABLED:
    'Inbound Mail is enabled for this program. If you disable this queue, mails received on the program will not be visible to agents. Do you want to continue?',
  ENABLE_FAX_QUEUE_INBOUND_DISABLED:
    'Inbound Fax is disabled. To receive faxes in the intake queue, you need to enable Inbound Fax in Program Configuration. Do you want to continue?',
  DISABLE_FAX_QUEUE_INBOUND_ENABLED:
    'Inbound Fax is enabled for this program. If you disable this queue, faxes received on the program will not be visible to agents. Do you want to continue?',
}

export const PAYSIGN_CONFIG_STRINGS = {
  ERROR_HEADING: 'Paysign Error!',
  ALREADY_ENROLLED_LOWERCASE: 'patient is already enrolled',
  ALREADY_ENROLLED: 'Already Enrolled',
  ENROLLMENT_PROCESSING_ERROR: 'Enrollment Processing Error',
  COPAY_VENDOR_ERROR: 'Co-pay Vendor API Error',
  UNKNOWN_ERR: 'Unknown Error',
  VENDOR_DISABLED_ERR: 'vendor is disabled',
  VENDOR_DISABLED_HEADER: 'VENDOR DISABLED',
}

export const AWS_STRINGS = {
  AMAZON_CONNECT: 'Amazon Connect',
  MULTIPLE_RECORDS: 'Multiple Records',
  INCOMING_CALL: 'Incoming Call',
  INCOMING_VOICEMAIL: 'Incoming Voicemail',
  INCOMING_CCB: 'Incoming Callback',
  ONGOING_CALL: 'Ongoing Call',
  UNKNOWN_CALLER: 'Unknown Caller',
  PROGRAM: 'Program',
  QUEUE: 'Queue',
  ACCEPT: 'Accept',
  VOICEMAIL: 'VoiceMail',
  VOICEMAIL_ID: '3645',
  CCB: 'CCB',
  CALL_BACK: 'Callback',
  OUTBOUND: 'Outbound',
  CTI_PERMISSION_KEY: 'cti_integration',
  TEVA_CARES_PROGRAM_ID: '4',
  AE_PC_NOTIFICATION: 'Potential AE/PC detected!',
  CCP_CHANNEL_NAME: 'aws-ccp-leader',
  LEADER_LOCK_KEY: 'aws_ccp_leader_lock',
  ONGOING_CALL_FLAG_KEY: 'aws_ccp_ongoing_call',
  LOCK_TTL_MS: 30 * 60 * 1000,
  POLL_INTERVAL_MS: 2000,
  MISSED_BEATS_BEFORE_TAKEOVER: 4,
  HEARTBEAT_INTERVAL_MS: 2000,
  HEARTBEAT_WAIT_MS: 2500,
  INBOUND: 'Inbound',
  REGION: 'us-east-1',
  HIDE_CCP: 'HIDE CCP',
  SHOW_CCP: 'SHOW CCP',
  VERBAL_MI_ALERT: 'Verbal MI',
}

export const DUPLICATION = {
  SCORE_ACCESSOR: 'score',
  SELECT_ACCESSOR: 'select',
  COMPONENT_TYPE: 'component',
  RADIO_TYPE: 'radio',
}

export const CONFIG_STRINGS = {
  ADVANCE_SEARCH_ENTITIES: [
    'c_patients',
    'c_providers',
    'c_organizations',
    'c_contacts',
    'c_cases',
    'c_facilities',
  ],
  ADVANCE_SEARCH_ENTITY_LABELS: [
    'patients',
    'providers',
    'organizations',
    'contacts',
    'case',
    'facilities',
  ],
}

export const MY_PROFILE_STRINGS = {
  MY_PROFILE: 'My Profile',
  AGENT_NAME: 'Agent Name',
  AGENT_EMAIL: 'Agent Email',
  PROFILE: 'Profile',
  ASSIGNED_PERMISSIONS_SET: 'Assigned Permissions Set',
  SEARCH_PERMISSIONS_SET: 'Search Permissions Set',
  ASSIGNED_PERMISSIONS_WITHIN_PERMISSION_SET:
    'Assigned Permissions Within Permission Set',
  NO_PERMISSIONS_MESSAGE: 'No permissions found.',
  PERMISSION_SETS_ERR_MSG: 'Error fetching permission sets:',
  PERMISSION_NAMES_ERR_MSG: 'Error fetching permission names:',
  READ_ONLY_USER: 'Read-Only User',
  PROGRAM_ADMIN: 'Program Admin',
  SYSTEM_ADMIN: 'System Admin',
  READ_ONLY_USER_ACCESS_MESSAGE: 'The user has view only access.',
  NEGATIVE_SCREEN_GLOBAL: 'You only have Program Permissions',
  NEGATIVE_SCREEN_PROGRAM: 'You only have Global Permissions',
}
export const EXPERIAN_STRINGS = {
  E_INCOME_CONSENT_TYPE: 'income estimator',
  PATIENT_FIRST_NAME: 'Patient First Name',
  PATIENT_LAST_NAME: 'Patient Last Name',
  PATIENT_STREET_ADDRESS: 'Patient Street Address',
  PATIENT_CITY: 'Patient City',
  PATIENT_STATE: 'Patient State',
  PATIENT_ZIP_CODE: 'Patient Zip Code',
  VALID_EINCOME_DETAILS_EXIST: 'Valid eIncome details already exist',
  VALID_EINCOME_DETAILS_EXIST_FOR_PATIENT:
    'Valid eIncome details already exist for this patient. Please check existing records.',
  VALID_EINCOME_CONSENT_NOT_AVAILABLE: 'Valid eIncome consent not available',
  CANNOT_FETCH_EINCOME_NO_CONSENT:
    'Cannot fetch eIncome details since valid eIncome consent not available for this patient.',
  PATIENT_VISIT_NUMBER: 'Patient Visit Number (Patient ID)',
  PATIENT_INFORMATION_MISSING: 'Patient information is missing.',
  REQUIRED_PATIENT_INFORMATION_MISSING:
    'Required patient information is missing. Following fields are required before requesting eIncome details',
  E_INCOME_BUTTON_TEXT: 'e-Income',
}

export const VERBAL_CONSENT_STRINGS = {
  RECORD_ID: 'Record ID',
  CONSENT_TYPE: 'Consent Type',
  SCRIPT_READ: 'Script Read',
  CONSENT_DECISION: 'Consent Decision',
  DECISION_DATE_TIME: 'Decision Date/Time',
  SCRIPT_TEMPLATE: 'Script Template',
  SCRIPT_VERSION: 'Script Version',
  START_DATE: 'Start Date',
  EXPIRATION_DATE: 'Expiration Date',
  CONSENTER_TYPE: 'Consenter Type',
  PATIENT_RELATIONSHIP: 'Patient Relationship',
  PROVIDER: 'Provider',
  PROVIDER_KEY_NAME: 'consenting_provider',
  SCRIPT_READ_NO_SCRIPT: 'No Script',
  SCRIPT_READ_YES: 'Yes',
  SCRIPT_READ_NO: 'No',
  CONSENT_DECISION_YES: 'Yes',
  CONSENT_DECISION_NO: 'No',
  CONSENTER_TYPE_OTHER: 'Other',
  CONSENTER_FIRST_NAME: 'Consenter First Name',
  CONSENTER_LAST_NAME: 'Consenter Last Name',
  CONSENTER_PHONE: 'Consenter Phone',
  INFO_SHARING_FIRST_NAME: 'Information Sharing First Name',
  INFO_SHARING_LAST_NAME: 'Information Sharing Last Name',
  NOTES: 'Notes',
  REVOKED: 'Revoked',
  TOAST_YES:
    'Verbal consent captured successfully and added to the Patient and Case records.',
  TOAST_NO: 'Verbal consent captured successfully.',
  EXPIRATION_BEFORE_START: 'Expiration date cannot be before start date',
  TRANSCRIPT_LETTER_CONFIRM:
    'Verbal consent was recorded as accepted. Do you want to send the Verbal Consent Transcript Letter to confirm this consent?',
  TRANSCRIPT_LETTER_CREATE_ERROR:
    'Error creating transcript letter outbound task.',
}

export const ENTITIES_WITH_NO_ADD_BUTTON = [
  'c_consent_requests',
  'c_patient_case_notes',
  'patient_case_notes',
]

export const ADVANCE_SEARCH_CONFIG_STRINGS = {
  ADVANCE_SEARCH_ENTITY_MAP: {
    c_patients: 'Patient',
    c_providers: 'Provider',
    c_organizations: 'Organization',
    c_contacts: 'Contact',
    c_cases: 'Case',
    c_facilities: 'Facility',
  },
}

export const REQUESTOR_MESSAGES = {
  REQUESTOR_SUCCESS: 'Requestor record fetched successfully',
  REQUESTOR_FAIL:
    'Requestor record not found, please use refresh button to try again',
}

export const PROCEDURE_NAME = {
  PROCESS_MULTICHANNEL_ATTACH_CASE: 'process_multichannel_attach_case',
}
